import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { CalendarEvent, Month, FilterResponse, FilterType, CombinedFilterData } from '../../helpers/common-types';
import { DATE_FORMAT, getDaysInMonth, getMonthsInRange, sortEventsChronologically } from '../../helpers/date-helpers';
import dayjs from 'dayjs';
const culture = '';

export type EventApiParameters = {
    startDate: string,
    endDate: string,
    productionSeasonId?: string
}

export type FilterApiParameters = {
    startDate: string,
    endDate: string,
    filterNames: FilterType[],
    promoCode: string
}

export type FilterResponseData = {
    filterName: FilterType,
    result: FilterResponse[] | undefined
}

const PromoApiPaths = {
    APPLY: "ApplyPromoCode",
    REMOVE: "RemovePromoCode",
    GET: "GetPromoCode"
}

export type PromoAppliedResult = {
    promoCode: string,
    text1: string,
    text2: string,
    text3: string,
    text4: string,
    text5: string,
    text6: string
}

export const apiSlice = createApi({
    reducerPath: 'api',
    baseQuery: fetchBaseQuery({
        baseUrl: `/ace-api/events/`
    }),
    tagTypes: ["Promo"],
    endpoints(builder) {
        return {
            getEvents: builder.query<Month[], EventApiParameters>({
                query: ({ startDate, endDate, productionSeasonId }) => `${culture ? `${culture}/` : ""}${productionSeasonId ? `${productionSeasonId}/` : ''}?startDate=${startDate}&endDate=${endDate}`,
                transformResponse: (response: CalendarEvent[], meta, arg) => {
                    const assembledCalendar = insertEventsIntoCalendar(response, arg);
                    return assembledCalendar;
                },
                providesTags: ["Promo"]
            }),
            getFilterOptions: builder.query<CombinedFilterData, FilterApiParameters>({
                /*
                 * Loop through all filter types and get all options
                 */
                queryFn: async ({ startDate, endDate, filterNames, promoCode }, _api, _extraOptions, fetchWithBQ) => {
                    const combinedResults: CombinedFilterData = await Promise.all(
                        filterNames.map(filterName => {
                            return fetchWithBQ(`filter${culture ? `/${culture}` : ""}?name=${filterName}&start=${startDate}&end=${endDate}&promoCode=${promoCode}`)
                        })
                    ).then(resultArray => resultArray.reduce((object, item, index) => {
                        return {
                            ...object,
                            [filterNames[index]]: item.data
                        }
                    }, {}));
                    return { data: combinedResults };
                },
                providesTags: ["Promo"]
            }),
            applyPromoCode: builder.mutation<PromoAppliedResult, string>({
                query: (code) => {
                    return {
                        url: `${PromoApiPaths.APPLY}`,
                        method: 'POST',
                        body: { promoCode: code },
                        headers: {
                            "Content-Type": "application/json; charset=utf-8"
                        }
                    }
                },
                invalidatesTags: ["Promo"]
            }),
            removePromoCode: builder.mutation<any, void>({
                query: () => {
                    return {
                        url: `${PromoApiPaths.REMOVE}`,
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded; charset=utf-8"
                        }
                    }
                },
                invalidatesTags: ["Promo"]
            }),
            getPromoCode: builder.query<any, void>({
                query: () => {
                    return {
                        url: `${PromoApiPaths.GET}`,
                        method: 'GET',
                        headers: {
                            "Content-Type": "application/x-www-form-urlencoded; charset=utf-8"
                        }
                    }
                }
            })
        }
    }
});

export const { useGetEventsQuery, useGetFilterOptionsQuery, useApplyPromoCodeMutation, useRemovePromoCodeMutation, useGetPromoCodeQuery } = apiSlice;

const buildEmptyCalendar = (range: EventApiParameters) => {
    const { startDate, endDate } = range;
    let calendar: Month[] = [];
    let allMonths = getMonthsInRange(startDate, endDate);
    allMonths.forEach(monthData => {
        monthData.days = getDaysInMonth(monthData.month);
        calendar.push(monthData);
    });
    return calendar;
}

const insertEventsIntoCalendar = (eventList: CalendarEvent[], dateRange: EventApiParameters) => {
    const sortedEvents = sortEventsChronologically(eventList);
    const calendar = buildEmptyCalendar(dateRange);
    sortedEvents.forEach(event => {
        const date = dayjs(event.eventDate);
        const month = date.startOf('month');
        const day = date.startOf('day');
        const monthGroup = calendar.filter(monthData => monthData.month == month.format(DATE_FORMAT));
        // extra null checks needed because API endpoint returns the full event list when not cached
        if (monthGroup.length > 0) {
            const dayGroup = monthGroup[0].days.filter(dayData => dayData.date == day.format(DATE_FORMAT));
            if (dayGroup.length > 0) {
                dayGroup[0].events.push(event);
            }
        }
    });
    calendar.forEach(month => {
        const totalEvents = month.days.filter(day => day.events.length > 0);
        month.anyEvents = totalEvents.length > 0;
    })
    return calendar;
}